import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { taskModule } from "@rentiohq/shared-frontend/dist/reduxV2/task";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import {
  EPropertyMemberTypes,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import {
  getIsShowExitChecklist,
  isEndedContract,
  isFutureContract,
} from "@rentiohq/shared-frontend/dist/utils/contract.utils";
import {
  memberHasOneOfRoles,
  memberHasRole,
} from "@rentiohq/shared-frontend/dist/utils/roles.utils";
import {
  Card,
  RentioInternalRenderer,
} from "@rentiohq/web-shared/dist/components";
import { useInternalMode } from "@rentiohq/web-shared/dist/redux/system/system.hooks";
import React from "react";
import { ConfirmLegalActivationModal } from "scenes/RentioLegal/components/ConfirmLegalActivationModal";
import { useIsRentioLegalActivated } from "scenes/RentioLegal/hooks/useIsRentioLegalActivated";
import { NumberParam, StringParam } from "serialize-query-params";
import { IndexContractModal } from "../../../../../../components/IndexContractModalV2";
import { ContractMetaData } from "./components/ContractMetaCard/ContractMetaData";
import { EntryAdministration } from "./components/EntryAdministration";
import { ExitAdministration } from "./components/ExitAdministration";
import { IndexationCard } from "./components/IndexationCard";
import { LinkedProperties } from "./components/LinkedProperties";
import { RentioLegalCard } from "./components/RentioLegalCard";
import { TasksCard } from "./components/TasksCard";
import * as S from "./ContractCard.styled";
import { getIndexationMessage } from "./ContractCard.utils";

interface IProps {
  contract: IContract;
  property: IProperty;
  showContractType?: boolean;
  showChecklistInitialState?: boolean;
  onCancelContract?: () => void;
}

export const ContractCard = (props: IProps) => {
  const {
    contract,
    property,
    showContractType = true,
    showChecklistInitialState = true,
    onCancelContract,
  } = props;
  const { internalModeEnabled } = useInternalMode();

  const showExitChecklist = getIsShowExitChecklist(contract);
  const contractIsEnded = isEndedContract(contract);

  const hasRentioLegal = useIsRentioLegalActivated();

  const [showEntryChecklistItems, setShowEntryChecklistItems] =
    React.useState(false);
  const [showExitChecklistItems, setShowExitChecklistItems] =
    React.useState(false);

  const [_, setQueryParamValue] = useQueryParams({
    documentId: StringParam,
    rentDepositId: NumberParam,
  });

  // Local state
  const [showIndexContractModal, setShowIndexContractModal] =
    React.useState(false);
  const [contractToEnableRentioLegalFor, setContractToEnableRentioLegalFor] =
    React.useState<IContract>();

  // Redux selectors
  const { isBroker, broker } = authHooks.useSelf();

  const hasPropertyManagement = property.members.some(member => {
    const isLoggedInBroker =
      isBroker && member.account.id === broker?.accountId;
    const hasMemberRole = memberHasRole(member, EPropertyMemberTypes.Broker);
    const hasManagerRole = memberHasOneOfRoles(member, [
      EPropertyMemberTypes.IndexationManager,
      EPropertyMemberTypes.FinancialManager,
      EPropertyMemberTypes.TechnicalManager,
    ]);

    return isLoggedInBroker && hasMemberRole && hasManagerRole;
  });

  React.useEffect(() => {
    if (!showChecklistInitialState) {
      return;
    }

    if (contractIsEnded) {
      setShowEntryChecklistItems(false);
      setShowExitChecklistItems(false);
      return;
    }

    setShowEntryChecklistItems(
      !showExitChecklist &&
        !(hasPropertyManagement && !isFutureContract(contract)),
    );

    if (showExitChecklist) {
      setShowExitChecklistItems(true);
    }
  }, [contractIsEnded, showExitChecklist, hasPropertyManagement]);

  const handleIndexModalClose = (documentId?: string) => {
    setShowIndexContractModal(false);
    setQueryParamValue({
      documentId,
    });
  };

  const handleActivateRentioLegal = async (contract: IContract) => {
    if (!hasRentioLegal) {
      return;
    }

    setContractToEnableRentioLegalFor(contract);
  };

  const { items: contractTasks = [] } = taskModule.hooks.usePaged({
    shouldRefetch: true,
    query: {
      page: 1,
      limit: CONFIG.DEFAULT_FETCH_LIMIT,
      filter: {
        and: [
          { contractId: { eq: contract.id } },
          { propertyId: { eq: property.id } },
          { finishedAt: { is: null } },
        ],
      },
    },
  });

  const { ibanisationStatus, linkedProperties = [] } = contract;
  const indexationItem = getIndexationMessage({
    property,
    contract,

    // Callbacks
    onIndexContract: () => setShowIndexContractModal(true),

    // Config
    internalModeEnabled,
  });

  // Render
  return (
    <>
      <S.ContractCard>
        <Card hasBorder={false}>
          <ContractMetaData
            contract={contract}
            property={property}
            showContractType={showContractType}
            onCancelContract={onCancelContract}
          />
        </Card>
        <S.Flex flexDirection="column">
          {linkedProperties.length > 0 && (
            <S.Card>
              <Card.Section>
                <LinkedProperties contract={contract} />
              </Card.Section>
            </S.Card>
          )}

          <RentioInternalRenderer
            items={{
              ibanisationStatus,
            }}
          />

          {indexationItem && (
            <IndexationCard
              indexationItem={indexationItem}
              contract={contract}
              property={property}
            />
          )}

          {contractTasks.length > 0 && (
            <S.Card>
              <TasksCard
                tasks={contractTasks}
                propertyId={property.id}
                contractId={contract.id}
              />
            </S.Card>
          )}

          <S.Card>
            <EntryAdministration
              contract={contract}
              property={property}
              showCircles={showChecklistInitialState && !contractIsEnded}
              showChecklistItems={showEntryChecklistItems}
              setShowChecklistItems={setShowEntryChecklistItems}
            />
          </S.Card>

          {showExitChecklist && (
            <S.Card>
              <ExitAdministration
                contract={contract}
                property={property}
                showCircles={showChecklistInitialState && !contractIsEnded}
                showChecklistItems={showExitChecklistItems}
                setShowChecklistItems={setShowExitChecklistItems}
              />
            </S.Card>
          )}

          {broker && (
            <S.Card>
              <RentioLegalCard
                contract={contract}
                onActivateRentioLegal={handleActivateRentioLegal}
              />
            </S.Card>
          )}
        </S.Flex>
      </S.ContractCard>

      {showIndexContractModal && (
        <IndexContractModal
          contractId={contract.id}
          onClose={handleIndexModalClose}
        />
      )}

      {contractToEnableRentioLegalFor && (
        <ConfirmLegalActivationModal
          contract={contractToEnableRentioLegalFor}
          onSuccess={() => setContractToEnableRentioLegalFor(undefined)}
          onClose={() => setContractToEnableRentioLegalFor(undefined)}
        />
      )}
    </>
  );
};
